import type { PersonaHookVariationSuggestion } from "@nexus/contracts";
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { HighlightText } from "@/features/opportunities/components/HighlightText";
import { humanizeConceptLabel } from "@/features/personas/lib/persona-format";
import { OriginalBriefLink } from "@/features/opportunities/components/scripts/OriginalBriefLink";
import { isImportedBriefScript } from "@/features/marketplace/lib/custom-asset";
import type { SentScriptMeta } from "@/features/marketplace/types/marketplace.types";
import type { PersonaScriptDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";
import { remainingAlternateHookCapacity } from "@/features/persona-opportunities/lib/hook-variations-eligibility";
import { scenesWithPreviewedHook } from "@/features/persona-opportunities/lib/persona-script-hook-preview";
import { canAnimate } from "@/lib/motion";

import { HookVariationsComparePanel } from "./HookVariationsComparePanel.js";
import { PersonaScriptActionsFooter } from "./PersonaScriptActionsFooter.js";
import { PersonaScriptHooksSection } from "./PersonaScriptHooksSection.js";
import {
  PersonaScriptSceneTable,
  type PersonaScriptSceneField,
} from "./PersonaScriptSceneTable.js";
import { PersonaScriptStoryboard } from "./PersonaScriptStoryboard.js";

export interface PersonaScriptHookCompare {
  variants: PersonaHookVariationSuggestion[];
  isConfirming: boolean;
  onConfirm: (keptIndexes: number[]) => void;
  onDiscard: () => void;
}

export interface PersonaScriptExpandedBodyProps {
  script: PersonaScriptDto;
  setId: string;
  tenantId: string | null;
  searchQuery: string;
  isSelected: boolean;
  canEdit: boolean;
  originalBriefUrl: string | null;
  editingKey: string | null;
  aiEditKey: string | null;
  flashKey: string | null;
  flashSource: "manual" | "ai";
  savingKey: string | null;
  aiBusyKey: string | null;
  onStartEdit: (key: string) => void;
  onStopEdit: () => void;
  onStartAi: (key: string) => void;
  onStopAi: () => void;
  onApplyAi: (key: string, instruction: string) => void;
  onSaveCell: (sceneNumber: number, field: PersonaScriptSceneField, value: string) => void;
  hookCompare: PersonaScriptHookCompare | null;
  showAddThreeHooks: boolean;
  isSuggesting: boolean;
  onAddThreeHooks: () => void;
  onAiEdit: () => void;
  onSendToVendor: () => void;
  isAiEditing: boolean;
  sentMeta: SentScriptMeta | null;
  orderHref?: string;
  onOpenOrder?: (
    orderId: string,
    orderScriptId: string | undefined,
    event: ReactMouseEvent<HTMLAnchorElement>,
  ) => void;
  /**
   * Hub drawer moves copy, share, AI edit, add-3-hooks, and send into the sheet header,
   * and lifts the hook suggestions to the top of the body next to them.
   */
  actionsPlacement?: "footer" | "header";
  /** Hub drawer uses the shared drawer type scale. Set-workspace cards stay on `card`. */
  surface?: "card" | "drawer";
  /** Hub drawer puts the storyboard above the ad description. */
  storyboardPlacement?: "end" | "above-description";
  /** Replaces the editable scene table in place, e.g. with a version-history preview. */
  scenePreview?: ReactNode;
  /** `sceneNumber:field` keys the editable scene table highlights as changed since v1. */
  changedCellKeys?: ReadonlySet<string>;
  /** False when the script already carries legacy separate hook-variant scripts. */
  canAddAlternateHooks?: boolean;
}

/** Unfolded script body shared by the set workspace and the hub drawer. */
export function PersonaScriptExpandedBody({
  script,
  setId,
  tenantId,
  searchQuery,
  isSelected,
  canEdit,
  originalBriefUrl,
  editingKey,
  aiEditKey,
  flashKey,
  flashSource,
  savingKey,
  aiBusyKey,
  onStartEdit,
  onStopEdit,
  onStartAi,
  onStopAi,
  onApplyAi,
  onSaveCell,
  hookCompare,
  showAddThreeHooks,
  isSuggesting,
  onAddThreeHooks,
  onAiEdit,
  onSendToVendor,
  isAiEditing,
  sentMeta,
  orderHref,
  onOpenOrder,
  actionsPlacement = "footer",
  surface = "card",
  storyboardPlacement = "end",
  scenePreview,
  changedCellKeys,
  canAddAlternateHooks = true,
}: PersonaScriptExpandedBodyProps) {
  const { t } = useTranslation();
  const [previewHookKey, setPreviewHookKey] = useState<string | null>(null);
  const previewedHook = script.hooks?.find(
    (hook) => hook.position > 1 && hook.hookKey === previewHookKey,
  );
  const isPreviewingHook = previewedHook != null;
  const hookCompareRef = useRef<HTMLDivElement>(null);
  const drawer = surface === "drawer";
  const hookCompareOnTop = actionsPlacement === "header";
  const isHookCompareOpen = hookCompare != null;

  // Suggestions land long after the header click; bring them to wherever the user scrolled.
  useEffect(() => {
    if (!isHookCompareOpen || !hookCompareOnTop) return;
    hookCompareRef.current?.scrollIntoView?.({
      block: "nearest",
      behavior: canAnimate() ? "smooth" : "auto",
    });
  }, [isHookCompareOpen, hookCompareOnTop]);
  const sectionLabel = drawer
    ? "text-caption font-medium tracking-label text-muted-foreground uppercase"
    : "text-[11px] font-medium uppercase tracking-wide text-muted-foreground";
  const blockLabel = drawer
    ? "mb-1 text-caption font-medium tracking-label text-muted-foreground uppercase"
    : "mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground";
  const bodyCopy = drawer
    ? "mt-1 whitespace-pre-wrap text-drawer-body text-foreground"
    : "mt-1 whitespace-pre-wrap leading-relaxed text-foreground";
  const hookComparePanel = hookCompare ? (
    <div ref={hookCompareRef} className="scroll-mt-4">
      <HookVariationsComparePanel
        currentHook={
          script.hook?.trim() ||
          t("personaOpportunities.scripts.untitledHook", {
            defaultValue: "Script {{slot}}",
            slot: script.slotIndex,
          })
        }
        variants={hookCompare.variants}
        alternateCapacity={remainingAlternateHookCapacity(script)}
        isConfirming={hookCompare.isConfirming}
        onConfirm={hookCompare.onConfirm}
        onDiscard={hookCompare.onDiscard}
      />
    </div>
  ) : null;
  const storyboard = tenantId && !isImportedBriefScript(script) ? (
    <PersonaScriptStoryboard
      script={script}
      setId={setId}
      tenantId={tenantId}
      labelClassName={drawer ? sectionLabel : undefined}
    />
  ) : null;

  return (
    <div
      className={
        drawer
          ? "space-y-4 text-foreground"
          : "space-y-4 border-t border-border bg-muted/20 px-4 py-4 text-sm"
      }
    >
      {hookCompareOnTop ? hookComparePanel : null}
      {storyboardPlacement === "above-description" ? storyboard : null}
      {(script.adDescription ||
        script.concept ||
        script.presenter ||
        script.background ||
        script.product ||
        originalBriefUrl) && (
        <div className="space-y-3 rounded-sm bg-muted px-4 py-4">
          {script.adDescription ? (
            <section>
              <h4 className={sectionLabel}>
                {t("personaOpportunities.scripts.adDescription", "Ad description")}
              </h4>
              <p className={bodyCopy}>
                <HighlightText text={script.adDescription} query={searchQuery} />
              </p>
            </section>
          ) : null}
          {script.concept ? (
            <section>
              <h4 className={sectionLabel}>
                {t("personaOpportunities.scripts.concept", "Concept")}
              </h4>
              <p className={bodyCopy}>
                <HighlightText text={humanizeConceptLabel(script.concept)} query={searchQuery} />
              </p>
            </section>
          ) : null}
          {originalBriefUrl ? (
            <section>
              <OriginalBriefLink url={originalBriefUrl} />
            </section>
          ) : null}
          {(script.presenter || script.background || script.product) && (
            <dl className={drawer ? "grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1.5 text-drawer-body" : "grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1.5 text-xs"}>
              {script.presenter ? (
                <>
                  <dt className="text-muted-foreground">
                    {t("personaOpportunities.scripts.presenter", "Presenter")}
                  </dt>
                  <dd className="text-foreground">{script.presenter}</dd>
                </>
              ) : null}
              {script.background ? (
                <>
                  <dt className="text-muted-foreground">
                    {t("personaOpportunities.scripts.background", "Background")}
                  </dt>
                  <dd className="text-foreground">{script.background}</dd>
                </>
              ) : null}
              {script.product ? (
                <>
                  <dt className="text-muted-foreground">
                    {t("personaOpportunities.scripts.product", "Product")}
                  </dt>
                  <dd className="text-foreground">{script.product}</dd>
                </>
              ) : null}
            </dl>
          )}
        </div>
      )}
      {scenePreview == null && !script.parentScriptId ? (
        <PersonaScriptHooksSection
          script={script}
          setId={setId}
          tenantId={tenantId}
          canEdit={canEdit}
          canAddAlternates={canAddAlternateHooks}
          isLocked={script.isHookSetLocked === true}
          previewHookKey={isPreviewingHook ? previewHookKey : null}
          onPreview={setPreviewHookKey}
          surface={surface}
        />
      ) : null}
      {scenePreview != null ? (
        scenePreview
      ) : script.scenes.length > 0 ? (
        <PersonaScriptSceneTable
          scenes={scenesWithPreviewedHook(script.scenes, previewedHook, (text) =>
            t("personaOpportunities.scripts.hooks.onScreen", { text, defaultValue: "On screen: {{text}}" }),
          )}
          isSelected={isSelected}
          canEdit={canEdit && !isPreviewingHook}
          surface={surface}
          editingKey={editingKey}
          aiEditKey={aiEditKey}
          flashKey={flashKey}
          flashSource={flashSource}
          savingKey={savingKey}
          aiBusyKey={aiBusyKey}
          onStartEdit={onStartEdit}
          onStopEdit={onStopEdit}
          onStartAi={onStartAi}
          onStopAi={onStopAi}
          onApplyAi={onApplyAi}
          onSaveCell={onSaveCell}
          changedCellKeys={changedCellKeys}
        />
      ) : null}
      {script.script?.trim() ? (
        <div className="rounded-sm bg-muted px-4 py-3.5">
          <p className={blockLabel}>
            {t("personaOpportunities.scripts.fullScript", "Full script")}
          </p>
          <p className={drawer ? "whitespace-pre-wrap text-drawer-body text-foreground" : "whitespace-pre-wrap text-xs leading-relaxed text-foreground"}>
            {script.script}
          </p>
        </div>
      ) : null}
      {script.rationale ? (
        <div className="rounded-sm bg-muted px-4 py-3.5">
          <p className={blockLabel}>
            {t("personaOpportunities.scripts.rationale", "Why this works")}
          </p>
          <p className={drawer ? "text-drawer-body text-foreground" : "text-xs leading-relaxed text-foreground"}>{script.rationale}</p>
        </div>
      ) : null}
      {hookCompareOnTop ? null : hookComparePanel}
      {storyboardPlacement === "end" ? storyboard : null}
      <PersonaScriptActionsFooter
        script={script}
        showAddThreeHooks={showAddThreeHooks}
        isSuggesting={isSuggesting}
        onAddThreeHooks={onAddThreeHooks}
        onAiEdit={onAiEdit}
        onSendToVendor={onSendToVendor}
        isAiEditing={isAiEditing}
        sentMeta={sentMeta}
        orderHref={orderHref}
        onOpenOrder={onOpenOrder}
        actionsPlacement={actionsPlacement}
      />
    </div>
  );
}
